/**
 * Pages
 *
 * Lazy-loaded page components for code splitting.
 * Each page is loaded on demand when its route is accessed.
 */
import { lazy } from 'react';

// Public pages
export const HomePage = lazy(() => import('./public/HomePage'));
export const AboutPage = lazy(() => import('./public/AboutPage'));
export const PricingPage = lazy(() => import('./public/PricingPage'));
export const PrivacyPage = lazy(() => import('./public/PrivacyPage'));
export const TermsPage = lazy(() => import('./public/TermsPage'));
export const ContactPage = lazy(() => import('./public/ContactPage'));

// Auth pages
export const SignInPage = lazy(() => import('./auth/SignInPage'));
export const SignUpPage = lazy(() => import('./auth/SignUpPage'));
export const ForgotPasswordPage = lazy(() => import('./auth/ForgotPasswordPage'));
export const ResetPasswordPage = lazy(() => import('./auth/ResetPasswordPage'));
export const VerifyEmailPage = lazy(() => import('./auth/VerifyEmailPage'));
export const OAuthCallbackPage = lazy(() => import('./auth/OAuthCallbackPage'));
export const MfaChallengePage = lazy(() => import('./auth/MfaChallengePage'));

// Invitation pages
export const InvitationAcceptPage = lazy(() => import('./invitations/InvitationAcceptPage'));

// Shared (public) pages
export const SharedReportViewerPage = lazy(() => import('./shared/SharedReportViewerPage'));

// Dashboard pages
export const DashboardPage = lazy(() => import('./dashboard/DashboardPage'));
export const ProfilePage = lazy(() => import('./dashboard/ProfilePage'));
export const WorkspacesPage = lazy(() => import('./dashboard/WorkspacesPage'));
export const PostsPage = lazy(() => import('./dashboard/PostsPage'));
export const CalendarPage = lazy(() => import('./dashboard/CalendarPage'));
export const MediaPage = lazy(() => import('./dashboard/MediaPage'));
export const CampaignsPage = lazy(() => import('./dashboard/CampaignsPage'));
export const InboxPage = lazy(() => import('./dashboard/InboxPage'));
export const MentionsPage = lazy(() => import('./dashboard/MentionsPage'));
export const AnalyticsPage = lazy(() => import('./dashboard/AnalyticsPage'));
export const KnowledgePage = lazy(() => import('./dashboard/KnowledgePage'));
export const KnowledgeDocumentDetailPage = lazy(() => import('./dashboard/knowledge/KnowledgeDocumentDetailPage'));
export const BrandVoicePage = lazy(() => import('./dashboard/brand-voice/BrandVoicePage'));
export const BrandVoiceFormPage = lazy(() => import('./dashboard/brand-voice/BrandVoiceFormPage'));
export const PersonasPage = lazy(() => import('./dashboard/personas/PersonasPage'));
export const PersonaFormPage = lazy(() => import('./dashboard/personas/PersonaFormPage'));
export const PersonaDetailPage = lazy(() => import('./dashboard/personas/PersonaDetailPage'));
export const AgentsDashboardPage = lazy(() => import('./dashboard/ai-agents/AgentsDashboardPage'));
export const AgentConfigPage = lazy(() => import('./dashboard/ai-agents/AgentConfigPage'));
export const AgentLogsPage = lazy(() => import('./dashboard/ai-agents/AgentLogsPage'));
export const SettingsPage = lazy(() => import('./dashboard/SettingsPage'));
export const ChannelsPage = lazy(() => import('./dashboard/ChannelsPage'));
export const NotificationCenterPage = lazy(() => import('./dashboard/notifications/NotificationCenterPage'));

// Campaign sub-pages
export const CampaignCreateSelectionPage = lazy(() => import('./dashboard/campaigns/CampaignCreateSelectionPage'));
export const CampaignCreatePage = lazy(() => import('./dashboard/campaigns/CampaignCreatePage'));
export const AICampaignCreatePage = lazy(() => import('./dashboard/campaigns/AICampaignCreatePage'));
export const CampaignDetailPage = lazy(() => import('./dashboard/campaigns/CampaignDetailPage'));
export const CampaignEditPage = lazy(() => import('./dashboard/campaigns/CampaignEditPage'));
export const CampaignComparisonPage = lazy(() => import('./dashboard/campaigns/CampaignComparisonPage'));

// Content sub-pages
export const ContentPage = lazy(() => import('./dashboard/content/ContentPage'));
export const ContentCreatePage = lazy(() => import('./dashboard/content/ContentCreatePage'));
export const ContentDetailPage = lazy(() => import('./dashboard/content/ContentDetailPage'));
export const ApprovalQueuePage = lazy(() => import('./dashboard/content/ApprovalQueuePage'));

// Publishing page
export const PublishingPage = lazy(() => import('./dashboard/publishing/PublishingPage'));

// Settings sub-pages
export const TeamPage = lazy(() => import('./dashboard/settings/TeamPage'));

// Profile tab pages
export const ProfileGeneralTab = lazy(() => import('./dashboard/profile/ProfileGeneralTab'));
export const ProfileSecurityTab = lazy(() => import('./dashboard/profile/ProfileSecurityTab'));
export const ProfilePreferencesTab = lazy(() => import('./dashboard/profile/ProfilePreferencesTab'));
export const ProfileSessionsTab = lazy(() => import('./dashboard/profile/ProfileSessionsTab'));
export const ProfileDataPrivacyTab = lazy(() => import('./dashboard/profile/ProfileDataPrivacyTab'));

// Ad Manager - AI Wizard
export const AIAdWizardPage = lazy(() => import('./dashboard/ad-manager/AIAdWizardPage'));

// Ad Manager - Creative Detail
export const AdCreativeDetailPage = lazy(() => import('./dashboard/ad-manager/AdCreativeDetailPage'));

// Error pages
export const NotFoundPage = lazy(() => import('./NotFoundPage'));

// Page Actions (non-lazy)
export { ReportsPageActions } from './dashboard/ReportsPageActions';